import React from "react";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { FontQuicksand } from "../../BuildingBlocks/FontQuicksand";
import { Text } from "../../BuildingBlocks/Text";
import { Background } from "../../BuildingBlocks/Background";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { CRMColors } from "../../../models/CRMColors";
import { CRMStatusPill } from "../CRMStatusPill/CRMStatusPill";
import { CRMEmptyPlaceholder } from "../CRMEmptyPlaceholder/CRMEmptyPlaceholder";

export type TCRMTableColumn<TRow> = {
    label: string;
    width: string;
    render: (row: TRow) => React.ReactNode;
};

export type TCRMTableSection<TRow> = {
    title?: string;
    rows: Array<TRow>;
    footer?: React.ReactNode;
};

type TCRMTableSheetProps<TRow> = {
    columns: Array<TCRMTableColumn<TRow>>;
    sections: Array<TCRMTableSection<TRow>>;
    getRowKey: (row: TRow) => string;
    getRowHref?: (row: TRow) => string;
    onRowClick?: (row: TRow) => void;
    emptyMessage?: string;
    footer?: React.ReactNode;
    fillHeight?: boolean;
};

export const CRMTableSheet = <TRow,>(props: TCRMTableSheetProps<TRow>): JSX.Element => {
    const childSize = props.columns.map((column) => column.width).join(" ");
    const isEmpty = props.sections.every((section) => section.rows.length === 0);

    return (
        <div className={`crm-table-sheet${props.fillHeight ? " crm-table-sheet--fill" : ""}`}>
            {/* HEADER */}
            <div className="crm-table-sheet__header">
                <SpacingRow spacing={CRMSpacing.MEDIUM} childSize={childSize}>
                    {props.columns.map((column, index) => (
                        <HeaderCell key={index}>{column.label}</HeaderCell>
                    ))}
                </SpacingRow>
            </div>

            {/* BODY */}
            <div className="crm-table-sheet__body">
                {isEmpty &&
                    <Background color={CRMColors.NEUTRAL_PAPER} padding="80px 0px 0px 0px" height="100%">
                        <CRMEmptyPlaceholder iconColor={CRMColors.NEUTRAL_8}>
                            {props.emptyMessage ?? "Nothing to show."}
                        </CRMEmptyPlaceholder>
                    </Background>
                }

                <SpacingColumn>
                    {props.sections
                        .filter((section) => section.rows.length > 0)
                        .map((section, sectionIndex) => (
                            <SpacingColumn key={sectionIndex}>
                                {section.title !== undefined && <CategoryTitle title={section.title} />}

                                <SpacingColumn>
                                    {section.rows.map((row) => (
                                        <Row
                                            key={props.getRowKey(row)}
                                            childSize={childSize}
                                            href={props.getRowHref ? props.getRowHref(row) : undefined}
                                            onClick={props.onRowClick ? () => props.onRowClick!(row) : undefined}
                                        >
                                            {props.columns.map((column, columnIndex) => (
                                                <RowCell key={columnIndex}>{column.render(row)}</RowCell>
                                            ))}
                                        </Row>
                                    ))}
                                </SpacingColumn>

                                {section.footer}
                            </SpacingColumn>
                        ))}

                    {props.footer}
                </SpacingColumn>
            </div>
        </div>
    );
};

const Row = (props: React.PropsWithChildren<{ childSize: string; href?: string; onClick?: () => void }>): JSX.Element => {
    const content =
        <SpacingRow spacing={CRMSpacing.MEDIUM} childSize={props.childSize} alignItems="center">
            {props.children}
        </SpacingRow>;

    return props.href !== undefined
        ? <a className="crm-table-sheet__row" href={props.href} onClick={props.onClick}>{content}</a>
        : <div className="crm-table-sheet__row" onClick={props.onClick}>{content}</div>;
};

const HeaderCell = (props: React.PropsWithChildren<{}>): JSX.Element =>
    <FontQuicksand>
        <Text size={14} weight={700} lineHeight={1.4}>
            {props.children}
        </Text>
    </FontQuicksand>;

const RowCell = (props: React.PropsWithChildren<{}>): JSX.Element =>
    <FontQuicksand>
        <Text size={14} weight={500} lineHeight={1.4}>
            {props.children}
        </Text>
    </FontQuicksand>;

const CategoryTitle = (props: { title: string }): JSX.Element =>
    <Background
        padding={`${CRMSpacing.MEDIUM} ${CRMSpacing.SMALL}`}
        borderBottomDefinition={`1px solid ${CRMColors.NEUTRAL_6}`}
    >
        <CRMStatusPill
            pillColor={CRMColors.PRIMARY_2}
            borderColor={CRMColors.PRIMARY_2}
            textColor={CRMColors.NEUTRAL_PAPER}
            label={props.title}
        />
    </Background>;
